import { useSuspenseQuery } from '@tanstack/react-query';
import type * as React from 'react';

import type { NavigationScreenHomeState } from '@gitbook/app-navigation';
import {
    Page,
    PageFooter,
    PageHeaderChromeless,
    PageMain,
    PageWallpaperSection,
} from '@gitbook/spine-react';
import { useHeadTitle } from '@gitbook/util-react-head';

import { APIErrorBoundary } from '../../Components';
import {
    NavigationRedirect,
    NavigationScreenWithOrganization,
    useSetAnalyticsScreen,
} from '../../navigation';
import { apiQuery } from '../../state';
import darkIntroImage from './assets/bg-home-dark.raw.svg';
import lightIntroImage from './assets/bg-home-light.raw.svg';
import { ActivitySection } from './components/Activity/ActivitySection';
import { DiscoverySection } from './components/Discovery/DiscoverySection';
import { HomeFooter } from './components/HomeFooter';
import { HomeHeader } from './components/HomeHeader';
import { HomeContextProvider } from './HomeContext';

/**
 * Home screen
 */
export function HomeScreen({ screen }: { screen: NavigationScreenHomeState }): React.ReactElement {
    useHeadTitle('Home');
    return (
        <APIErrorBoundary
            fallback={{
                404: (
                    <NavigationRedirect
                        to={{ screen: { type: 'root', organization: undefined } }}
                        replace
                    />
                ),
            }}
        >
            <NavigationScreenWithOrganization screen={screen}>
                {({ screen }) => <HomeScreenWithOrganization screen={screen} />}
            </NavigationScreenWithOrganization>
        </APIErrorBoundary>
    );
}

/**
 * Home screen for an organization
 */
function HomeScreenWithOrganization(props: {
    screen: NavigationScreenHomeState & { organization: string };
}) {
    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', { organizationId: props.screen.organization })
    );

    useSetAnalyticsScreen(
        {
            screen: 'organization_home',
            organization: organization.id,
        },
        [organization.id]
    );

    return (
        <HomeContextProvider organization={organization}>
            <Page>
                <PageWallpaperSection
                    imageLight={lightIntroImage}
                    imageDark={darkIntroImage}
                    className="bg-cover bg-top pb-10"
                >
                    <PageHeaderChromeless className="py-4">
                        <HomeHeader />
                    </PageHeaderChromeless>

                    <ActivitySection />
                </PageWallpaperSection>

                <PageMain data-testid="home.screen">
                    <DiscoverySection />
                </PageMain>

                <PageFooter>
                    <HomeFooter />
                </PageFooter>
            </Page>
        </HomeContextProvider>
    );
}
